// ponytail: CSS imported at app-level index.css — bare imports here get tree-shaken by sideEffects:["*.css"]
import {
  TabPanel,
  type TabPanelHeaderTemplateOptions,
  TabView,
  type TabViewProps,
  type TabPanelProps,
} from 'primereact/tabview';
import { twMerge } from 'tailwind-merge';
import React from 'react';
import { FontWeight, Heading } from '../../tokens';
import { LiliTooltip, TooltipPosition } from '../LiliTooltip';

export interface LiliTabProps extends TabPanelProps {
  title: string;
  content?: React.ReactNode;
  id?: string;
  testId?: string;
  disabled?: boolean;
  toolTipData?: { content: string; position: TooltipPosition };
  leftComponent?: React.ReactNode;
  rightComponent?: React.ReactNode;
}

export interface LiliTabsProps extends TabViewProps {
  tabs: LiliTabProps[];
  activeIndex?: number;
  onTabIndexChanged?: (idx: number, id?: string) => void;
  fullWidthTabs?: boolean;
}

export const LiliTabs = (props: LiliTabsProps) => {
  const { tabs, onTabIndexChanged, fullWidthTabs, activeIndex, ...rest } = props;

  const tabViewNavContentClass = 'overflow-visible w-full';
  const tabViewNavContainerClass = 'overflow-visible w-full';
  const tabViewNavClass = 'bg-transparent flex flex-row w-full';
  const tabWidthClass = fullWidthTabs ? 'w-full' : '';

  const handleTabChange = (e: { index: number }) => {
    if (onTabIndexChanged) {
      onTabIndexChanged(e.index, tabs[e.index]?.id || '');
    }
  };

  const renderHeader = (options: TabPanelHeaderTemplateOptions, liliTabProps: LiliTabProps) => {
    const { selected, onClick } = options;
    const { title, id, testId, disabled, toolTipData, leftComponent, rightComponent } =
      liliTabProps;

    return (
      <>
        <LiliTooltip description={toolTipData?.content || ''} targetId={title} />
        <button
          data-tooltip-id={toolTipData?.content ? title : undefined}
          type='button'
          onClick={(event) => {
            if (disabled) return;
            onClick(event);
          }}
          className={twMerge(
            'group p-tabview-title text-center line-h-[150%] p-[6px]',
            fullWidthTabs ? 'w-full' : 'w-fit '
          )}
          id={id}
          data-testid={testId}
          disabled={disabled}>
          <div
            className={twMerge(
              'py-[12px] rounded-[4px] text-text-main-secondary px-[18px] max-h-[40px]',
              !disabled && 'hover:bg-surface-primary-bw-02 hover:text-text-main-primary',
              selected && 'px-[16.5px]',
              fullWidthTabs ? 'w-full' : 'flex flex-row gap-[4px] items-center'
            )}>
            {leftComponent}
            <Heading.H10
              className='text-inherit whitespace-nowrap overflow-hidden text-ellipsis'
              fontWeight={selected ? FontWeight.SEMIBOLD : FontWeight.REGULAR}>
              {title}
            </Heading.H10>
            {rightComponent}
          </div>
          <div
            className={twMerge(
              'relative -bottom-[8px] left-1/2 w-0 h-[2px] bg-surface-brand-action-03 transition-all duration-300',
              selected && 'w-[calc(100%+12px)] -left-[6px]'
            )}></div>
        </button>
      </>
    );
  };

  return (
    <>
      <TabView
        activeIndex={activeIndex || 0}
        onTabChange={onTabIndexChanged ? handleTabChange : undefined}
        panelContainerClassName='border-t-[1px] border-t-outline-primary-bw-03 bg-transparent p-0 box-border mt-[-1px] w-full'
        pt={{
          navContent: { className: tabViewNavContentClass },
          navContainer: { className: tabViewNavContainerClass },
          nav: { className: tabViewNavClass },
          root: { className: 'w-full' },
        }}
        {...rest}>
        {tabs.map((tab: LiliTabProps) => (
          <TabPanel
            headerTemplate={(options: TabPanelHeaderTemplateOptions) => renderHeader(options, tab)}
            key={tab.title}
            header={tab.title}
            pt={{
              root: { className: tabWidthClass },
            }}>
            {tab.content}
          </TabPanel>
        ))}
      </TabView>
    </>
  );
};
